import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Spin } from 'antd';
import type { LogCode } from '../hooks/usePunchClock';

interface KioskPanelProps {
  logCode: LogCode;
  modeLabel: string;
  employeeName: string;
  statusMessage: string;
  loading: boolean;
  flashBig: boolean;
  onSwitchMode: (code: LogCode) => void;
  onPunch: (cardValue: string) => void;
  onExit: () => void;
}

const KioskPanel: React.FC<KioskPanelProps> = React.memo(({
  modeLabel,
  employeeName,
  statusMessage,
  loading,
  flashBig,
  onSwitchMode,
  onPunch,
  onExit,
}) => {
  const [cardInput, setCardInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input field
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(() => {
    if (cardInput.trim()) {
      onPunch(cardInput);
      setCardInput('');
    }
    inputRef.current?.focus();
  }, [cardInput, onPunch]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  // Get current date and weekday
  const now = new Date();
  const dateStr = now.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
  const weekdayStr = weekdays[now.getDay()];

  return (
    <div className="kiosk-panel">
      {/* Date display (BR-I01) */}
      <div className="kiosk-date">{dateStr}</div>
      <div className="kiosk-weekday">{weekdayStr}</div>

      {/* Mode label with flash effect (BR-T01) */}
      <div
        className="kiosk-mode-label"
        style={{ fontSize: flashBig ? 28 : 24 }}
      >
        {modeLabel}
      </div>

      {/* Card input (BR-V01) */}
      <div className="kiosk-input-area">
        <Spin spinning={loading}>
          <input
            ref={inputRef}
            className="kiosk-card-input"
            type="password"
            value={cardInput}
            onChange={(e) => setCardInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="請刷卡..."
            autoFocus
          />
        </Spin>
      </div>

      {/* Employee name display (BR-V03) */}
      <div className="kiosk-employee-name">{employeeName}</div>

      {/* Status message (BR-C01/C02) */}
      <div className="kiosk-status-message">{statusMessage}</div>

      {/* Mode buttons (BR-B01~B05) */}
      <div className="kiosk-buttons">
        <Button
          type="primary"
          onClick={() => onSwitchMode('Q')}
        >
          上班簽到
        </Button>
        <Button onClick={() => onSwitchMode('S')}>
          外出簽退
        </Button>
        <Button onClick={() => onSwitchMode('T')}>
          進入簽到
        </Button>
        <Button onClick={() => onSwitchMode('V')}>
          下班簽退
        </Button>
        <Button danger onClick={onExit}>
          離開
        </Button>
      </div>
    </div>
  );
});

KioskPanel.displayName = 'KioskPanel';

export default KioskPanel;
